import apiClient from './client';
import type { ApiResponse } from '../types';

export interface MonthlyGridDay {
  day: number;
  date: string;
  is_holiday: boolean;
  status: 'present' | 'late' | 'absent' | 'holiday' | 'future' | 'week_off' | 'half_day' | 'leave';
  check_in_time: string | null;
  check_out_time: string | null;
  check_in_photo: string | null;
  check_out_photo: string | null;
  check_in_distance_meters: number | null;
  working_hours: number | null;
  working_seconds?: number | null;
  is_late: boolean;
  minutes_late: number;
  location_name: string | null;
  attendance_id?: number | null;
  raw_status?: string | null;
  is_week_off?: boolean;
  is_manual?: boolean;
  is_edited?: boolean;
  leave_paid?: boolean | null;
  missing_checkout?: boolean;
  on_duty?: boolean;
  shift_set?: boolean;
  late_min?: number;
  early_min?: number;
  ot_min?: number;
  extra_day?: number;
  day_total_pay?: number;
}

export interface StaffGridRow {
  staff: {
    id: number;
    staff_code: string;
    full_name: string;
    phone: string;
    role: string;
    profile_picture: string | null;
    salary_amount: number;
    salary_type: string;
  };
  present_count: number;
  late_count: number;
  absent_count: number;
  holiday_count: number;
  total_hours: number;
  total_seconds?: number;
  week_off_count?: number;
  half_day_count?: number;
  leave_count?: number;
  missing_checkout_count?: number;
  ot_minutes?: number;
  shift_set?: boolean;
  salary_paid?: boolean;
  days: MonthlyGridDay[];
}

export interface HolidayItem {
  id: number;
  holiday_date: string;
  description: string | null;
}

export interface MonthlyGridResponse {
  month: string;
  total_days: number;
  start_date: string;
  end_date: string;
  holidays: HolidayItem[];
  grid: StaffGridRow[];
}

export interface LateRecord {
  date: string;
  check_in_time: string;
  minutes_late: number;
}

export interface StaffSalarySummary {
  staff_id: number;
  staff_code: string;
  full_name: string;
  role: string;
  monthly_salary: number;
  daily_rate: number;
  total_days: number;
  holiday_days: number;
  present_days: number;
  late_days: number;
  absent_days: number;
  payable_days: number;
  base_salary: number;
  late_records: LateRecord[];
  unpaid_advance: number;
  adjustment: number;
  adjustment_reason: string;
  advance_deducted: number;
  final_amount: number;
  is_paid: boolean;
  payment_details: any;
  salary_type?: string;
  shift_set?: boolean;
  week_off_days?: number;
  extra_days?: number;
  extra_day_pay?: number;
  half_days?: number;
  paid_leave_days?: number;
  unpaid_leave_days?: number;
  missing_checkout_days?: number;
  late_minutes?: number;
  late_deduction?: number;
  early_minutes?: number;
  early_deduction?: number;
  ot_minutes?: number;
  ot_amount?: number;
  calculated_salary?: number;
}

export interface SalarySummaryResponse {
  month: string;
  total_days: number;
  holidays_count: number;
  holidays: HolidayItem[];
  staff_salaries: StaffSalarySummary[];
}

export interface RecordPaymentPayload {
  staff_id: number;
  month_year: string;
  total_days: number;
  present_days: number;
  late_days: number;
  absent_days: number;
  holiday_days: number;
  base_salary: number;
  adjustment: number;
  adjustment_reason?: string;
  advance_deducted: number;
  final_amount: number;
  payment_mode: 'cash' | 'upi' | 'bank' | 'other';
  payment_reference?: string;
  notes?: string;
  week_off_days?: number;
  extra_days?: number;
  late_minutes?: number;
  late_deduction?: number;
  early_minutes?: number;
  early_deduction?: number;
  ot_minutes?: number;
  ot_amount?: number;
}

export interface PaymentHistoryItem {
  id: number;
  staff_id: number;
  staff_name: string;
  staff_code: string;
  staff_role: string;
  month_year: string;
  total_days: number;
  present_days: number;
  late_days: number;
  absent_days: number;
  holiday_days: number;
  base_salary: number;
  adjustment: number;
  adjustment_reason: string | null;
  advance_deducted: number;
  final_amount: number;
  payment_mode: string;
  payment_reference: string | null;
  paid_at: string;
  paid_by_name: string | null;
  notes: string | null;
}

export const staffAttendanceMgmtAPI = {
  // Monthly attendance grid
  getMonthlyGrid: async (month?: string): Promise<ApiResponse<MonthlyGridResponse>> => {
    const res = await apiClient.get('/staff-attendance-mgmt/monthly-grid', {
      params: { month },
    });
    return res.data;
  },

  // Staff-wise drilldown details
  getStaffDetails: async (staffId: number, month?: string): Promise<ApiResponse<any>> => {
    const res = await apiClient.get(`/staff-attendance-mgmt/staff-details/${staffId}`, {
      params: { month },
    });
    return res.data;
  },

  // Salary calculations summary
  getSalarySummary: async (month?: string): Promise<ApiResponse<SalarySummaryResponse>> => {
    const res = await apiClient.get('/staff-attendance-mgmt/salary-summary', {
      params: { month },
    });
    return res.data;
  },

  // Holidays
  getHolidays: async (month?: string): Promise<ApiResponse<HolidayItem[]>> => {
    const res = await apiClient.get('/staff-attendance-mgmt/holidays', {
      params: { month },
    });
    return res.data;
  },

  addHoliday: async (payload: { holiday_date: string; description?: string }): Promise<ApiResponse<any>> => {
    const res = await apiClient.post('/staff-attendance-mgmt/holidays', payload);
    return res.data;
  },

  deleteHoliday: async (id: number): Promise<ApiResponse<any>> => {
    const res = await apiClient.delete(`/staff-attendance-mgmt/holidays/${id}`);
    return res.data;
  },

  // Salary Payments
  recordPayment: async (payload: RecordPaymentPayload): Promise<ApiResponse<any>> => {
    const res = await apiClient.post('/staff-attendance-mgmt/salary/record-payment', payload);
    return res.data;
  },

  getPaymentHistory: async (params?: { month?: string; staff_id?: number }): Promise<ApiResponse<PaymentHistoryItem[]>> => {
    const res = await apiClient.get('/staff-attendance-mgmt/salary/payment-history', {
      params,
    });
    return res.data;
  },
};

// ━━━ Attendance v3: manual attendance, edits, shifts & rules ━━━
export type ManualStatus = 'present' | 'absent' | 'half_day' | 'leave';

export interface ManualAttendancePayload {
  staff_id: number;
  date: string;
  status: ManualStatus;
  check_in?: string | null;
  check_out?: string | null;
  leave_paid?: boolean | null;
  reason: string;
}

export interface EditAttendancePayload {
  status?: ManualStatus | 'late';
  check_in?: string | null;
  check_out?: string | null;
  leave_paid?: boolean | null;
  reason: string;
}

export interface ShiftScheduleItem {
  id: number;
  staff_id: number;
  shift_start: string | null;
  shift_end: string | null;
  week_off_day: number | null;
  effective_from: string;
  notes: string | null;
  created_by_name?: string | null;
  created_at: string;
}

export interface StaffShiftRow {
  staff: { id: number; staff_code: string; full_name: string; role: string; salary_amount: number; salary_type: string };
  current: ShiftScheduleItem | null;
  upcoming: ShiftScheduleItem[];
  history: ShiftScheduleItem[];
}

export interface AttendanceEditLogItem {
  id: number;
  action: string;
  old_status: string | null;
  new_status: string | null;
  old_check_in_time: string | null;
  new_check_in_time: string | null;
  old_check_out_time: string | null;
  new_check_out_time: string | null;
  reason: string;
  edited_by_name: string | null;
  salary_was_paid: number;
  created_at: string;
}

export const attendanceAdminAPI = {
  markManual: async (payload: ManualAttendancePayload): Promise<ApiResponse<any>> => {
    const res = await apiClient.post('/attendance-admin/manual', payload);
    return res.data;
  },
  editRecord: async (id: number, payload: EditAttendancePayload): Promise<ApiResponse<any>> => {
    const res = await apiClient.patch(`/attendance-admin/records/${id}`, payload);
    return res.data;
  },
  getRecordHistory: async (id: number): Promise<ApiResponse<AttendanceEditLogItem[]>> => {
    const res = await apiClient.get(`/attendance-admin/records/${id}/history`);
    return res.data;
  },
  getShifts: async (): Promise<ApiResponse<StaffShiftRow[]>> => {
    const res = await apiClient.get('/attendance-admin/shifts');
    return res.data;
  },
  setShift: async (payload: {
    staff_id: number; shift_start: string | null; shift_end: string | null;
    week_off_day: number | null; effective_from: string; notes?: string | null;
  }): Promise<ApiResponse<any>> => {
    const res = await apiClient.post('/attendance-admin/shifts', payload);
    return res.data;
  },
  deleteShift: async (id: number): Promise<ApiResponse<any>> => {
    const res = await apiClient.delete(`/attendance-admin/shifts/${id}`);
    return res.data;
  },
  getRules: async (): Promise<ApiResponse<{ grace_minutes: number; ot_min_minutes: number }>> => {
    const res = await apiClient.get('/attendance-admin/rules');
    return res.data;
  },
  updateRules: async (payload: { grace_minutes: number; ot_min_minutes: number }): Promise<ApiResponse<any>> => {
    const res = await apiClient.put('/attendance-admin/rules', payload);
    return res.data;
  },
};
